import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, Calendar, TrendingUp, Shield, Search, Crown, Trash2, Edit, BarChart3 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { format, parseISO } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0 });
  const [events, setEvents] = useState({ events: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showMembershipDialog, setShowMembershipDialog] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (user.role !== 'admin') {
        toast.error('Admin access required');
        navigate('/');
      } else {
        fetchDashboard();
        fetchUsers();
        fetchEvents();
      }
    }
  }, [user, authLoading, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (search = '') => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/admin/users${params}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/admin/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchQuery);
  };

  const handleUpdateRole = async (role) => {
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/role?role=${role}`);
      toast.success(`User role updated to ${role}`);
      setShowRoleDialog(false);
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleUpdateMembership = async (tier) => {
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/membership?membership_tier=${tier}`);
      toast.success(`Membership updated to ${tier}`);
      setShowMembershipDialog(false);
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update membership');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success('Event deleted');
      fetchEvents();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const getMembershipLabel = (tier) => {
    const labels = {
      'free': 'Complimentary',
      'attendee_premium': 'Attendee Premium',
      'business_premium': 'Business Premium'
    };
    return labels[tier] || tier;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-secondary/30 py-12">
      <div className="container-custom max-w-7xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-gold" />
          <div>
            <h1 className="text-3xl font-semibold">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage users, events, and platform settings</p>
          </div>
        </div>

        {/* Stats Overview */}
        {dashboard && (
          <div className="grid md:grid-cols-4 gap-6 mb-10">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-3xl font-semibold">{dashboard.users.total}</p>
                  </div>
                  <Users className="w-10 h-10 text-gold opacity-50" />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {dashboard.users.by_tier.business_premium} Business • {dashboard.users.by_tier.attendee_premium} Attendee Premium
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Events</p>
                    <p className="text-3xl font-semibold">{dashboard.events.total}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-gold opacity-50" />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {dashboard.events.upcoming} upcoming
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Registrations</p>
                    <p className="text-3xl font-semibold">{dashboard.registrations.total}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-gold opacity-50" />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {dashboard.registrations.rsvps} RSVPs • {dashboard.registrations.tickets} Tickets
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Premium Members</p>
                    <p className="text-3xl font-semibold">
                      {dashboard.users.by_tier.attendee_premium + dashboard.users.by_tier.business_premium}
                    </p>
                  </div>
                  <Crown className="w-10 h-10 text-gold opacity-50" />
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {Math.round((dashboard.users.by_tier.attendee_premium + dashboard.users.by_tier.business_premium) / dashboard.users.total * 100)}% of users
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="events">Events</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>View and manage all registered users</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearch} className="flex gap-2 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, email, or organization..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button type="submit">Search</Button>
                </form>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Name</th>
                        <th className="text-left py-3 px-2">Email</th>
                        <th className="text-left py-3 px-2">Organization</th>
                        <th className="text-left py-3 px-2">Membership</th>
                        <th className="text-left py-3 px-2">Role</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.users.map((u) => (
                        <tr key={u.id} className="border-b hover:bg-secondary/50">
                          <td className="py-3 px-2">{u.full_name}</td>
                          <td className="py-3 px-2">{u.email}</td>
                          <td className="py-3 px-2">{u.organization || '-'}</td>
                          <td className="py-3 px-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              u.membership_tier === 'business_premium' ? 'bg-gold/20 text-gold' :
                              u.membership_tier === 'attendee_premium' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {getMembershipLabel(u.membership_tier)}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            {u.role === 'admin' ? (
                              <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-800">Admin</span>
                            ) : (
                              <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-800">User</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedUser(u); setShowRoleDialog(true); }}
                              >
                                <Shield className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSelectedUser(u); setShowMembershipDialog(true); }}
                              >
                                <Crown className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Showing {users.users.length} of {users.total} users
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader>
                <CardTitle>Event Management</CardTitle>
                <CardDescription>View and manage all events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Event</th>
                        <th className="text-left py-3 px-2">Date</th>
                        <th className="text-left py-3 px-2">Organizer</th>
                        <th className="text-left py-3 px-2">Registrations</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.events.map((event) => (
                        <tr key={event.id} className="border-b hover:bg-secondary/50">
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-medium">{event.title}</p>
                              <p className="text-xs text-muted-foreground">{event.category}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2">{format(parseISO(event.date), 'MMM d, yyyy')}</td>
                          <td className="py-3 px-2">{event.organizer_name}</td>
                          <td className="py-3 px-2">{event.total_registrations || 0}</td>
                          <td className="py-3 px-2">
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/events/${event.id}`)}
                              >
                                <BarChart3 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => handleDeleteEvent(event.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  Showing {events.events.length} of {events.total} events
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Role Dialog */}
        <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update User Role</DialogTitle>
              <DialogDescription>
                Change the role for {selectedUser?.full_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Button
                variant={selectedUser?.role === 'admin' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => handleUpdateRole('admin')}
              >
                <Shield className="w-4 h-4 mr-2" /> Admin
              </Button>
              <Button
                variant={selectedUser?.role !== 'admin' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => handleUpdateRole('user')}
              >
                <Users className="w-4 h-4 mr-2" /> Regular User
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Membership Dialog */}
        <Dialog open={showMembershipDialog} onOpenChange={setShowMembershipDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Membership</DialogTitle>
              <DialogDescription>
                Change the membership tier for {selectedUser?.full_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Button
                variant={selectedUser?.membership_tier === 'free' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => handleUpdateMembership('free')}
              >
                Complimentary
              </Button>
              <Button
                variant={selectedUser?.membership_tier === 'attendee_premium' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => handleUpdateMembership('attendee_premium')}
              >
                <Crown className="w-4 h-4 mr-2" /> Attendee Premium
              </Button>
              <Button
                variant={selectedUser?.membership_tier === 'business_premium' ? 'default' : 'outline'}
                className="w-full"
                onClick={() => handleUpdateMembership('business_premium')}
              >
                <Crown className="w-4 h-4 mr-2" /> Business Premium
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
